import {useEffect} from "react";
import orderBy from "lodash/orderBy";
import {getLoadedPageCount, mergeRows, needsMoreRows} from "./paging";

// Backend calls made in a row to fill the pager window after one page change; stops a backend that
// keeps answering with few rows and a cursor from looping.
export const MAX_CHAINED_FETCHES = 3;

/** Paging slice shared by the Excalibur, Filing and Transaction Details table reducers. */
export const PAGED_ROWS_INITIAL_STATE = {
    rows: [],
    lastEvaluatedKey: null,
    pageCount: 1,
    currentPageIndex: 1,
    fetching: false,
    chainedFetches: 0,
    requestToken: 0,
    // The backend page the table component should fetch next, {startKey, requestToken}; null when none is due.
    fetchRequest: null,
    sortingColumn: {sortingField: "startTimestamp"},
    sortingDescending: true,
    loading: true,
    initialize: true
};

const fetchRequestFor = (startKey, requestToken) => ({startKey, requestToken});

export const sortRows = (rows, {sortingColumn, sortingDescending}) =>
    orderBy(rows, sortingColumn.sortingField, sortingDescending ? "desc" : "asc");

/** Asks for page one, once. */
export function initializePagedRows(state) {
    if (!state.initialize) {
        return state;
    }
    return {
        ...state,
        initialize: false,
        fetching: true,
        loading: true,
        fetchRequest: fetchRequestFor(null, state.requestToken)
    };
}

/** Drops the loaded rows and asks for page one under a new token, so a late answer to the old list is ignored. */
export function reloadPagedRows(state) {
    const requestToken = state.requestToken + 1;
    return {
        ...state,
        rows: [],
        lastEvaluatedKey: null,
        pageCount: 1,
        currentPageIndex: 1,
        fetching: true,
        chainedFetches: 0,
        requestToken,
        fetchRequest: fetchRequestFor(null, requestToken),
        loading: true,
        initialize: false
    };
}

/** Merges one backend page in and re-sorts the loaded list; chains another fetch while the pager window is short. */
export function receivePagedRows(state, {requestToken, data, lastEvaluatedKey}, rowKey) {
    if (requestToken !== state.requestToken) {
        return state;
    }
    const rows = sortRows(mergeRows(state.rows, data, rowKey), state);
    const pageCount = getLoadedPageCount(rows);
    const nextKey = lastEvaluatedKey || null;
    let currentPageIndex = state.currentPageIndex;
    const chain = needsMoreRows(currentPageIndex, pageCount, nextKey) && state.chainedFetches < MAX_CHAINED_FETCHES;
    if (!chain) {
        // The page stepped onto turned out not to exist: back onto the last loaded one.
        currentPageIndex = Math.min(currentPageIndex, pageCount);
    }
    return {
        ...state,
        rows,
        pageCount,
        lastEvaluatedKey: nextKey,
        currentPageIndex,
        fetching: chain,
        chainedFetches: chain ? state.chainedFetches + 1 : 0,
        fetchRequest: chain ? fetchRequestFor(nextKey, state.requestToken) : null,
        loading: chain && currentPageIndex > pageCount,
        initialize: false
    };
}

/** A page fetch failed: clear the spinner and step back onto the last loaded page. */
export function failPagedRows(state, {requestToken}) {
    if (requestToken !== state.requestToken) {
        return state;
    }
    return {
        ...state,
        fetching: false,
        chainedFetches: 0,
        fetchRequest: null,
        loading: false,
        initialize: false,
        currentPageIndex: Math.min(state.currentPageIndex, state.pageCount)
    };
}

/** Moves to `pageIndex`, asking for the next backend page when the pager window would run past the loaded rows. */
export function changePagedRowsPage(state, pageIndex) {
    const hasMore = Boolean(state.lastEvaluatedKey);
    let index = pageIndex;
    if (index > state.pageCount && !hasMore) {
        index = state.pageCount;
    }
    const startFetch = !state.fetching && needsMoreRows(index, state.pageCount, hasMore);
    const fetching = state.fetching || startFetch;
    return {
        ...state,
        currentPageIndex: index,
        fetching,
        chainedFetches: 0,
        fetchRequest: startFetch ? fetchRequestFor(state.lastEvaluatedKey, state.requestToken) : state.fetchRequest,
        loading: fetching && index > state.pageCount
    };
}

/** The user chose a new order: the whole loaded list is re-sorted. */
export function sortPagedRows(state, {sortingField, isDescending}) {
    const sorted = {
        ...state,
        sortingDescending: isDescending,
        sortingColumn: {sortingField}
    };
    return {
        ...sorted,
        rows: sortRows(state.rows, sorted)
    };
}

export const PAGE_FETCH_FAILED_MESSAGE = "Could not load the table. Reload the page to try again.";

/** Dispatcher for one page call: tags the answer with its request token; a failure becomes AUDIT_FETCH_FAILED and carries the dispatcher for its banner's dismiss. */
export const pagedRowsResponder = (dispatcher, requestToken, extra = {}) => action => {
    const failed = action.type === "FAILURE_API_RESPONSE";
    return dispatcher({
        type: failed ? "AUDIT_FETCH_FAILED" : action.type,
        payload: {...action.payload, ...extra, requestToken, ...(failed ? {dispatcher} : {})}
    });
};

/** Runs `fetchPage(startKey, requestToken)` for each fetch the reducer asked for; the reducer itself never calls the API. */
export function usePagedRowsFetch(fetchRequest, fetchPage) {
    useEffect(() => {
        if (fetchRequest) {
            fetchPage(fetchRequest.startKey, fetchRequest.requestToken);
        }
    }, [fetchRequest, fetchPage]);
}
